import { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './card';
import { Badge } from './badge';
import { Button } from './button';
import { Input } from './input';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Search, ChevronUp, ChevronDown, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

interface Column<T> {
  key: keyof T | string;
  header: string;
  hint?: string;
  width?: string;
  maxWidth?: string;
  align?: 'left' | 'center' | 'right';
  render?: (value: any, row: T, index: number) => JSX.Element | string | number | null;
  className?: string;
  sortable?: boolean;
  searchable?: boolean;
  // Use when `key` is a display-only label rather than a real field on the row
  // (e.g. a composite column, or a value with an `avg ?? latest` fallback).
  sortAccessor?: (row: T) => string | number | null | undefined;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  title?: string;
  description?: string;
  itemsPerPage?: number;
  maxHeight?: string;
  onRowClick?: (row: T, index: number) => void;
  rowClassName?: (row: T, index: number) => string;
  emptyMessage?: string;
  loading?: boolean;
  className?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  defaultSort?: {
    key: keyof T | string;
    direction: 'asc' | 'desc';
  };
}

export function DataTable<T extends Record<string, any>>({
  data,
  columns,
  title,
  description,
  itemsPerPage = 10,
  maxHeight = '400px',
  onRowClick,
  rowClassName,
  emptyMessage = 'No data available',
  loading = false,
  className = '',
  searchable = false,
  searchPlaceholder = 'Search...',
  defaultSort
}: DataTableProps<T>) {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{
    key: keyof T | string;
    direction: 'asc' | 'desc';
  } | null>(defaultSort || null);

  const filteredData = useMemo(() => {
    if (!searchable || !searchTerm) return data;

    return data.filter(row => {
      return columns.some(column => {
        if (column.searchable === false) return false;

        const value = row[column.key as keyof T];
        if (value == null) return false;

        return String(value).toLowerCase().includes(searchTerm.toLowerCase());
      });
    });
  }, [data, searchTerm, columns, searchable]);

  const sortedData = useMemo(() => {
    if (!sortConfig) return filteredData;

    const sortColumn = columns.find(col => col.key === sortConfig.key);
    const getSortValue = (row: T) =>
      sortColumn?.sortAccessor ? sortColumn.sortAccessor(row) : row[sortConfig.key as keyof T];

    const direction = sortConfig.direction === 'asc' ? 1 : -1;

    return [...filteredData].sort((a, b) => {
      const aValue = getSortValue(a);
      const bValue = getSortValue(b);

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return direction * (aValue - bValue);
      }

      return direction * String(aValue).localeCompare(String(bValue), undefined, {
        numeric: true,
        sensitivity: 'base'
      });
    });
  }, [filteredData, sortConfig, columns]);

  const handleSort = (columnKey: keyof T | string) => {
    const column = columns.find(col => col.key === columnKey);
    if (!column?.sortable) return;

    setSortConfig(prev => {
      if (prev?.key === columnKey) {
        if (prev.direction === 'asc') {
          return { key: columnKey, direction: 'desc' };
        } else {
          return null;
        }
      } else {
        return { key: columnKey, direction: 'asc' };
      }
    });
  };

  const totalPages = Math.max(1, Math.ceil(sortedData.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = sortedData.slice(startIndex, endIndex);

  const goToFirstPage = () => setCurrentPage(1);
  const goToPreviousPage = () => setCurrentPage(prev => Math.max(1, prev - 1));
  const goToNextPage = () => setCurrentPage(prev => Math.min(totalPages, prev + 1));
  const goToLastPage = () => setCurrentPage(totalPages);

  useEffect(() => {
    setCurrentPage(1);
  }, [sortedData.length, searchTerm, sortConfig]);

  const renderCellValue = (column: Column<T>, row: T, index: number) => {
    if (column.render) {
      return column.render(row[column.key as keyof T], row, index);
    }
    return row[column.key as keyof T] || '';
  };

  const getAlignmentClass = (align?: string) => {
    switch (align) {
      case 'center': return 'text-center';
      case 'right': return 'text-right';
      default: return 'text-left';
    }
  };

  const getHeaderAlignmentClass = (align?: string) => {
    switch (align) {
      case 'center': return 'justify-center';
      case 'right': return 'justify-end';
      default: return 'justify-start';
    }
  };

  if (loading) {
    return (
      <Card className={className}>
        {(title || description) && (
          <CardHeader>
            {title && <CardTitle>{title}</CardTitle>}
            {description && <CardDescription>{description}</CardDescription>}
          </CardHeader>
        )}
        <CardContent>
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      {(title || description) && (
        <CardHeader>
          {title && <CardTitle>{title}</CardTitle>}
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
      )}
      <CardContent className="p-0">
        {searchable && (
          <div className="p-4 border-b border-border">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
              <Input
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        )}

        <div
          className="relative overflow-auto border-t border-border"
          style={{ maxHeight }}
        >
          <table className="w-full table-auto">
            <thead className="sticky top-0 z-10 bg-accent/95 backdrop-blur-sm border-b border-border">
              <tr>
                {columns.map((column, index) => (
                  <th
                    key={String(column.key) + index}
                    className={`p-3 font-semibold text-foreground uppercase tracking-wider ${column.className || ''} ${column.sortable ? 'cursor-pointer hover:bg-accent/80 select-none' : ''}`}
                    style={{
                      width: column.width,
                      maxWidth: column.maxWidth,
                      minWidth: '100px'
                    }}
                    onClick={() => column.sortable && handleSort(column.key)}
                  >
                    <div className={`flex items-center ${getHeaderAlignmentClass(column.align)}`}>
                       <span title={`${column.hint ? column.hint  : ''}`}>{column.header}</span>
                      {/* <div className='relative'>
                      <span className={`${column.hint ? 'pr-6' : ''}`}>{column.header}</span>

                      {column.hint && (
                        <div className="absolute top-1 right-0 z-10">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Info className="w-4 h-4 text-muted-foreground cursor-help hover:text-foreground transition-colors" />
                            </TooltipTrigger>
                            <TooltipContent
                              side="top"
                              align="center"
                              className="max-w-[300px] text-xs bg-popover text-foreground border border-border shadow-lg"
                              sideOffset={8}
                            >
                              {column.hint}
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      )}
                      </div> */}
                      {column.sortable && (
                        <div className="flex flex-col ml-1">
                          <ChevronUp
                            className={`h-3 w-3 ${sortConfig?.key === column.key && sortConfig?.direction === 'asc'
                                ? 'text-primary'
                                : 'text-muted-foreground/50'
                              }`}
                          />
                          <ChevronDown
                            className={`h-3 w-3 -mt-1 ${sortConfig?.key === column.key && sortConfig?.direction === 'desc'
                                ? 'text-primary'
                                : 'text-muted-foreground/50'
                              }`}
                          />
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {currentData.length > 0 ? (
                currentData.map((row, index) => (
                  <tr
                    key={index}
                    className={`
                      border-b border-border hover:bg-accent transition-colors
                      ${onRowClick ? 'cursor-pointer' : ''}
                      ${rowClassName ? rowClassName(row, index) : ''}
                    `}
                    onClick={() => onRowClick?.(row, startIndex + index)}
                  >
                    {columns.map((column, colIndex) => (
                      <td
                        key={String(column.key) + colIndex}
                        className={`p-3 ${getAlignmentClass(column.align)}`}
                        style={{
                          maxWidth: column.maxWidth || '200px'
                        }}
                      >
                        <div className="truncate" title={String(row[column.key as keyof T] || '')}>
                          {renderCellValue(column, row, startIndex + index)}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="p-8 text-center text-muted-foreground">
                    {emptyMessage}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {(sortedData.length > itemsPerPage || (searchable && searchTerm)) && (
          <div className="flex items-center justify-between p-4 border-t border-border bg-card">
            <div className="flex items-center gap-4">
              <div className="text-sm text-muted-foreground">
                Showing {startIndex + 1} to {Math.min(endIndex, sortedData.length)} of {sortedData.length} entries
                {searchable && searchTerm && (
                  <span className="ml-2 text-muted-foreground/70">
                    (filtered from {data.length} total)
                  </span>
                )}
              </div>
              {searchable && searchTerm && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSearchTerm('')}
                  className="h-6 px-2 text-xs"
                >
                  Clear search
                </Button>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={goToFirstPage}
                disabled={currentPage === 1}
                className="h-8 w-8 p-0"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className="h-8 w-8 p-0"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              <div className="flex items-center space-x-1">
                <span className="text-sm text-muted-foreground">Page</span>
                <span className="text-sm font-medium">
                  {currentPage} of {totalPages}
                </span>
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
                className="h-8 w-8 p-0"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={goToLastPage}
                disabled={currentPage === totalPages}
                className="h-8 w-8 p-0"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}